import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar as CalendarIcon, Flame, Hourglass, Swords, ChevronRight } from 'lucide-react';
import { api, type CalendarEntryRow, type MatchCardStatus } from '../lib/api';
import { cn } from '../lib/cn';
import { displayClanName } from '../lib/displayName';

// ─────────────────────────────────────────────────────────────────────────────
// Calendar — the caller's own fixtures and this stage's results.
//
// For clan leaders, captains and team members: the matches scheduled for THEM
// (Lisa, 2026-07-22). Reads CalendarEntry rows by USER#discordId — the backend
// takes the identity from the token and accepts no user id, so this is always
// and only your own list.
//
// Two sections, in this order:
//   UPCOMING — soonest first, so the next match you must turn up for is the
//              first thing on the page.
//   PAST     — newest first, going down to older. Scoped by the backend to the
//              comp's CURRENTLY LIVE stage: once a stage finishes, its matches
//              stop appearing here at all.
//
// Each row links into the live Match Card.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_THEME: Record<MatchCardStatus, { bg: string; border: string; label: string; text: string; pulse: boolean }> = {
  DRAFT:     { bg: 'bg-iron-800',          border: 'border-iron-600',     label: 'DRAFT',         text: 'text-iron-300',    pulse: false },
  SCHEDULED: { bg: 'bg-iron-800',          border: 'border-iron-600',     label: 'SCHEDULED',     text: 'text-iron-200',    pulse: false },
  VETO:      { bg: 'bg-amber-900/30',      border: 'border-amber-600/60', label: 'VETO OPEN',     text: 'text-amber-300',   pulse: false },
  LIVE:      { bg: 'bg-forge-900/40',      border: 'border-forge-500/70', label: 'LIVE NOW',      text: 'text-forge-300',   pulse: true  },
  CLOSED:    { bg: 'bg-iron-800',          border: 'border-iron-600',     label: 'CLOSED',        text: 'text-iron-200',    pulse: false },
  OCR:       { bg: 'bg-blue-900/30',       border: 'border-blue-600/60',  label: 'OCR',           text: 'text-blue-300',    pulse: true  },
  VERIFIED:  { bg: 'bg-emerald-900/30',    border: 'border-emerald-600/60', label: 'VERIFIED',    text: 'text-emerald-300', pulse: false },
  FORGED:    { bg: 'bg-iron-700',          border: 'border-iron-500',     label: 'FORGED',        text: 'text-forge-300',   pulse: false },
  CANCELLED: { bg: 'bg-iron-800',          border: 'border-red-700/40',   label: 'CANCELLED',     text: 'text-red-400',     pulse: false },
  DISPUTED:  { bg: 'bg-red-900/30',        border: 'border-red-600/60',   label: 'DISPUTED',      text: 'text-red-300',     pulse: false },
  // W-A defect 3.
  FAILED:    { bg: 'bg-red-950/50',        border: 'border-red-700/70',   label: 'FAILED',        text: 'text-red-400',     pulse: false },
  // Depleted-org-tokens fork (orgTokenFallback) — same defect class as FAILED.
  MANUAL:    { bg: 'bg-iron-800',          border: 'border-amber-600/40', label: 'MANUAL',        text: 'text-amber-200',   pulse: false },
};

function formatWhen(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

function relativeBucket(ts: number): string {
  const ms = ts - Date.now();
  if (ms < -60 * 60 * 1000) return 'Earlier';
  if (ms <= 0) return 'Now';
  const hours = ms / (60 * 60 * 1000);
  if (hours < 1) return `In ${Math.round(ms / 60000)}m`;
  if (hours < 24) return `In ${Math.round(hours)}h`;
  const days = Math.round(hours / 24);
  return `In ${days}d`;
}

export function Calendar() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['calendar', 14],
    queryFn: () => api.calendar(14),
    refetchInterval: 30000,
  });

  // The backend already returns both lists in display order — upcoming soonest
  // first, past newest first (shared/calendarWindow.ts's splitCalendarEntries,
  // which is where the ordering is unit-tested). Re-sorted here anyway so the
  // page can't be knocked out of order by an older cached response that
  // predates that split.
  const upcoming = useMemo(
    () => [...(data?.entries ?? [])].sort((a, b) => a.scheduledAt - b.scheduledAt),
    [data?.entries],
  );
  const past = useMemo(
    () => [...(data?.past ?? [])].sort((a, b) => b.scheduledAt - a.scheduledAt),
    [data?.past],
  );
  const nothingAtAll = upcoming.length === 0 && past.length === 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex items-center gap-3">
        <CalendarIcon size={24} className="text-forge-400" />
        <h1 className="font-display text-2xl font-extrabold uppercase tracking-wider text-white">Your Calendar</h1>
        <span className="font-mono text-xs text-iron-500">next 14 days</span>
      </header>

      {isLoading && <p className="font-mono text-sm text-iron-400">Loading…</p>}
      {isError && <p className="font-mono text-sm text-red-400">Failed to load calendar.</p>}

      {!isLoading && !isError && nothingAtAll && (
        <div className="rounded-xl border border-iron-700 bg-iron-900 px-6 py-12 text-center">
          <p className="font-mono text-sm text-iron-400">No upcoming match cards. Schedule a match and it'll show up here.</p>
        </div>
      )}

      {!isLoading && !isError && !nothingAtAll && (
        <>
          <SectionHeading label="Upcoming" />
          {upcoming.length === 0 ? (
            <p className="mb-8 font-mono text-sm text-iron-500">Nothing scheduled in the next 14 days.</p>
          ) : (
            <ul className="mb-8 space-y-3">
              {upcoming.map(entry => <CalendarTile key={entry.sk} entry={entry} />)}
            </ul>
          )}

          {past.length > 0 && (
            <>
              {/* Scoped by the backend to the comp's LIVE stage — when a stage
                  ends, this section empties out on its own and the heading
                  disappears with it. The caption says so, otherwise a leader
                  whose stage just closed reads the empty list as data loss. */}
              <SectionHeading label="Past" caption="this stage" />
              <ul className="space-y-3">
                {past.map(entry => <CalendarTile key={entry.sk} entry={entry} />)}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

function SectionHeading({ label, caption }: { label: string; caption?: string }) {
  return (
    <div className="mb-3 flex items-baseline gap-2">
      <h2 className="font-display text-xs font-black uppercase tracking-[0.24em] text-iron-400">{label}</h2>
      {caption && <span className="font-mono text-[10px] uppercase tracking-wider text-iron-600">{caption}</span>}
    </div>
  );
}

function CalendarTile({ entry }: { entry: CalendarEntryRow }) {
  // Unmapped statuses render neutral with the raw value as label — a server
  // status the frontend doesn't know yet must not crash the whole calendar.
  const theme = STATUS_THEME[entry.cardStatus]
    ?? { bg: 'bg-iron-800', border: 'border-iron-600', label: String(entry.cardStatus), text: 'text-iron-300', pulse: false };
  // T3 (personal-upload-squad-select, 2026-07-24): tapping a match from the
  // calendar now opens the player's own mobile-first upload view — never the
  // shared desktop Match Card, which is what caused mis-filed uploads onto
  // other players' slots in the first place. PersonalUpload.tsx links back
  // out to the full card for anyone (leader/captain/admin) who needs veto,
  // roster init, comments, or forge.
  const linkTo = `/matchcard/${encodeURIComponent(entry.matchId)}/upload?compId=${encodeURIComponent(entry.compId)}`;
  return (
    <li>
      <Link
        to={linkTo}
        className={cn(
          'flex flex-col rounded-xl border-2 px-4 py-3 transition-all hover:border-forge-500/60',
          theme.bg, theme.border,
          theme.pulse && 'shadow-[0_0_30px_-10px_rgba(var(--accent-rgb),0.5)]',
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <span className={cn('inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-wider', theme.text)}>
            {theme.pulse ? <Flame size={12} className="animate-pulse" /> : <Hourglass size={12} />}
            {theme.label}
          </span>
          <span className="font-mono text-[11px] text-iron-400">{relativeBucket(entry.scheduledAt)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-wider text-white">
            <Swords size={14} className="text-iron-500" />
            <span>{entry.myClanId ? displayClanName(entry.myClanId.replace(/^CLAN#/, '')) : 'YOU'}</span>
            <span className="text-iron-500">vs</span>
            <span>{displayClanName(entry.opponentClanName)}</span>
          </div>
          <ChevronRight size={16} className="text-iron-500" />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[11px] text-iron-400">
          <span>{formatWhen(entry.scheduledAt)}</span>
          <span className="text-iron-500">·</span>
          <span className="font-bold uppercase text-iron-300">{entry.format}</span>
          <span className="text-iron-500">·</span>
          <span className="uppercase">{entry.role}</span>
          {entry.compName && (<><span className="text-iron-500">·</span><span>{entry.compName}</span></>)}
          {/* Demo-data tag (2026-07-21) — GET /calendar doesn't filter demo
              matches out (unlike /competitions), it just tags them.
              Typography-only, muted iron tone — no background fill, matches
              this row's own plain-text meta idiom (format/role/compName). */}
          {entry.isDemo && (<><span className="text-iron-500">·</span><span className="font-bold uppercase tracking-wider text-iron-500">Demo</span></>)}
        </div>
      </Link>
    </li>
  );
}

export default Calendar;
